import React, {useMemo} from "react";
import {RemoteValue} from "../../../util/RemoteValue";
import {useDeskStore} from "../../../api/DeskStore";
import {useNeighbourhoodStore} from "../../../api/NeighbourhoodStore";
import {EmployeeNeighbourhood} from "../../../api/EmployeeNeighbourhoodStore";
import {associateBy} from "../../../util/MapUtil";
import {idFn} from "../../../util/FunctionUtil";
import {DashboardWidget, DashboardWidgetItem} from "../DashboardWidget";
import {AppPages, usePageUrl} from "../../../app-layout/AppPages";
import {Stack, Typography} from "@mui/material";
import DeskChip from "../../../components/desk-chip/DeskChip";
import EmployeeAvatar from "../../../components/employee-avatar/EmployeeAvatar";
import NeighbourhoodColorIcon from "../../../components/neighbourhood-icon/NeighbourhoodColorIcon";
import {Desk, EmployeeRef, Neighbourhood, UNASSIGNED_NEIGHBOURHOOD_ID} from "../../../api/Model";
import {mapNotNull} from "../../../util/ArrayUtil";
import {Desks_Read, Neighbourhoods_Read} from "../../../util/access/Permission";
import {CsvModel} from "../../../util/CSVWriteUtil";
import {useFixDeskNeighbourhoodMismatches} from "./useFixDeskNeighbourhoodMismatches";

export type DeskNeighbourhoodMismatchProblem = {
    desk: Desk & {assignedTo: EmployeeRef},
    deskHood: Neighbourhood | undefined,
    employeeHoods: Neighbourhood[],
};

const deskNeighbourhoodMismatchCsvModel = RemoteValue.fromValue(new CsvModel<DeskNeighbourhoodMismatchProblem>([
    {header: 'Desk', value: ({desk}) => desk.name},
    {header: 'Room', value: ({desk}) => desk.roomName},
    {header: 'Desk Neighbourhood', value: ({deskHood}) => deskHood?.name ?? ''},
    {header: 'Person', value: ({desk}) => desk.assignedTo?.fullName ?? ''},
    {header: 'Employee Neighbourhoods', value: ({employeeHoods}) => employeeHoods.map(({name}) => name).join(', ')},
]));

export function DeskNeighbourhoodMismatchWidget() {
    const pageUrl = usePageUrl();
    const desks = useDeskStore(state => state.remoteValue);
    const neighbourhoods = useNeighbourhoodStore(state => state.remoteValue);
    const problems = useMemo(() =>
        RemoteValue.combine({desks, neighbourhoods})
            .map(({desks, neighbourhoods}) => {
                    const neighbourhoodsById = associateBy(neighbourhoods, idFn);
                    const employeeNeighbourhoods = EmployeeNeighbourhood.from(neighbourhoods);
                    const neighbourhoodless = neighbourhoodsById.get(UNASSIGNED_NEIGHBOURHOOD_ID);
                    const neighbourhoodlessA = neighbourhoodless ? [neighbourhoodless] : [];
                    const assignedDesks = desks.filter((desk): desk is Desk & {assignedTo: EmployeeRef} => desk.assignedTo != null);
                    return mapNotNull(assignedDesks, desk => {
                        if (!desk.assignedTo) return null;
                        const deskHood = desk.neighbourhoodId
                            ? neighbourhoodsById.get(desk.neighbourhoodId)
                            : neighbourhoodless;
                        const employeeHoods = employeeNeighbourhoods.get(desk.assignedTo);
                        const mismatch = deskHood
                            ? !employeeHoods.some(neighbourhood => neighbourhood.key === deskHood.key)
                            : employeeHoods.length > 0;
                        return mismatch
                            ? {
                                desk,
                                deskHood,
                                employeeHoods: employeeHoods.length > 0
                                    ? employeeHoods :
                                    neighbourhoodlessA,
                            }
                            : undefined;
                    });
                }
            ), [desks, neighbourhoods]);

    const fixAction = useFixDeskNeighbourhoodMismatches(problems);

    return (
        <DashboardWidget
            title="Desk and neighbourhood mismatches"
            description="A desk can be assigned to both an employee and a neighbourhood. Employees can belong to one or more neighbourhoods. This widget lists desks whose neighbourhood does not match any of its employee's neighbourhoods."
            problems={problems}
            requiredPermissions={[Desks_Read, Neighbourhoods_Read]}
            link={pageUrl(AppPages.desks, {}, {problem: 'neighbourhood-mismatch'})}
            csvModel={deskNeighbourhoodMismatchCsvModel}
            fixAction={fixAction}
            renderProblem={({desk, deskHood, employeeHoods}, index) => (
                <DashboardWidgetItem
                    key={desk.id}
                    index={index}
                    left={desk.assignedTo && (
                        <>
                            <EmployeeAvatar employee={desk.assignedTo} showNameTooltip sx={{width: 24, height: 24}}/>
                            {employeeHoods.length > 0 ? employeeHoods.map(neighbourhood => (
                                <Stack key={neighbourhood.key} direction="row" alignItems="center" gap={0.5}>
                                    <NeighbourhoodColorIcon neighbourhood={neighbourhood} size={16}/>
                                    <Typography variant="body2">{neighbourhood.name}</Typography>
                                </Stack>
                            )) : (
                                <Typography variant="body2" color="text.secondary">
                                    No employee neighbourhood
                                </Typography>
                            )}
                        </>
                    )}
                    right={
                        <>
                            {deskHood ? (
                                <Stack direction="row" alignItems="center" gap={0.5}>
                                    <NeighbourhoodColorIcon neighbourhood={deskHood} size={16}/>
                                    <Typography variant="body2">{deskHood.name}</Typography>
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">No desk neighbourhood</Typography>
                            )}
                            <DeskChip desk={desk} deskAssignee={desk.assignedTo} hideAssignee size="small"/>
                        </>
                    }
                />
            )}
        />
    );
}
